<script setup lang="ts">
import { useNuxtApp } from "#app";
import { ResizeType } from "@bitpatty/imgproxy-url-builder";
import ShopProductSmallPreview from "~/components/card/ShopProductSmallPreview.vue";

const props = defineProps({
    title: String,
    text: String,
    products: Array,
    detailUrl: String,
    skeleton: Boolean,
    notLazyLoad: Boolean,
    targetBlank: Boolean,
});
const nuxtApp = useNuxtApp();
</script>

<template>
    <div v-if="skeleton === false" class="flex flex-col card-preview-review block bg-gray-100 p-4 sm:p-6 rounded-lg space-y-4 h-full">
        <nuxt-link :to="detailUrl" :target="targetBlank ? '_blank' : ''" class="grow space-y-4">
            <h2 class="md:text-2xl uppercase text-gray-900 font-bold">{{ title }}</h2>
            <p v-if="text" class="text-gray-900">{{ text }}</p>
        </nuxt-link>
        <div>
            <ClientOnly>
                <Swiper
                    class="gallery"
                    :modules="[SwiperNavigation, SwiperEffectCreative]"
                    navigation
                    :space-between="15"
                    :slides-per-view="2"
                    :loop="false"
                    :breakpoints="{
                                    '1280': {
                                        slidesPerView: 3,
                                    },
                                }"
                >
                    <SwiperSlide
                        v-for="(productGroup, index) in products"
                        :key="index"
                        :class="{'border-yellow-400': productGroup.highlight, 'border-white': !productGroup.highlight}"
                        class="border-2 bg-white p-4 rounded-lg flex-col justify-between h-initial-important"
                    >
                        <ShopProductSmallPreview
                            :title="productGroup.product.title"
                            :picture-url="productGroup.product.image"
                            :price="productGroup.product.price"
                            :old-price="productGroup.product.oldPrice"
                            :detail-url="productGroup.product.url"
                        />
                    </SwiperSlide>
                </Swiper>
            </ClientOnly>
        </div>
        <div class="flex justify-center">
            <nuxt-link :to="detailUrl"
                       class="inline-block px-6 py-2 transition ease-in duration-200 uppercase rounded-full hover:bg-indigo-800 text-indigo-800 hover:text-white border-2 border-indigo-800 focus:outline-none text-indigo-800">
                <div class="flex justify-center items-center">
                    <p class="pr-2 text-sm font-medium leading-none">Read More</p>
                    <svg class="fill-stroke" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.75 12.5L10.25 8L5.75 3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </div>
            </nuxt-link>
        </div>
    </div>
    <div v-else class="flex flex-col bg-gray-50 p-4 sm:p-6 rounded-lg space-y-2 md:space-y-4 h-full">
        <div class="animate-pulse rounded h-4 w-[70%] bg-gray-100"></div>
        <div class="space-y-2">
            <div class="animate-pulse rounded h-2 w-[40%] bg-gray-100"></div>
            <div class="animate-pulse rounded h-2 w-[80%] bg-gray-100"></div>
            <div class="animate-pulse rounded h-2 w-[60%] bg-gray-100"></div>
        </div>
        <picture class="block relative mb-6">
            <div class="animate-pulse h-20 sm:h-40 w-full object-center rounded bg-soft-white-100"></div>
        </picture>
        <div class="animate-pulse rounded h-4 w-[70%] bg-gray-100"></div>
    </div>
</template>